import { useEffect, useRef, DependencyList, useReducer, useCallback } from 'react';
import isEqual from 'lodash/isEqual';

// This hook is a bit experimental.

interface UseAsyncCallbackResponse<R> {
	data: R | null;
	loading: boolean;
	error: any;
}

enum actionTypes {
	request,
	success,
	failure,
}

type Action<R> =
	| { type: actionTypes.request }
	| { type: actionTypes.success; results: R }
	| { type: actionTypes.failure; error: any };

export function useAsyncCallback<R = unknown>(
	callback: () => Promise<R>,
	dependencies: DependencyList
): UseAsyncCallbackResponse<R> {
	const reducer = useCallback((state: UseAsyncCallbackResponse<R>, action: Action<R>) => {
		switch (action.type) {
			case actionTypes.request:
				return state.loading ? state : { data: null, loading: true, error: null };
			case actionTypes.success:
				return { data: action.results, loading: false, error: null };
			case actionTypes.failure:
				return { data: null, loading: false, error: action.error };
		}
	}, []);

	const [state, dispatch] = useReducer(reducer, { data: null, loading: true, error: null });
	const ref = useRef<DependencyList>();
	ref.current = dependencies;

	useEffect(
		() => {
			dispatch({ type: actionTypes.request });
			let _canceled = false;
			Promise.resolve(callback())
				.then((response) => {
					if (!_canceled && isEqual(dependencies, ref.current)) {
						dispatch({ type: actionTypes.success, results: response });
					}
				})
				.catch((error) => {
					!_canceled && dispatch({ type: actionTypes.failure, error });
					console.error(error);
				});
			return () => {
				_canceled = true;
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[...dependencies]
	);

	return state;
}
